import {
    Button,
    Flex,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import type { TagType } from '@modules/product-tagging/type';
import type { Image } from '~/modules/page-builder/type/message-passing';
import ProductTaggingShell from '~/modules/product-tagging/components/product-tagging-shell/ProductTaggingShell';
import { useProductTagging } from '~/modules/product-tagging/context';
import TagPopover from '../tag-popover/TagPopover';
import * as S from './DetailSingleImageShell.style';
import HoverButtonsImage from './HoverButtonsImage';
import ImageCaption from '../../common/image-caption/ImageCaption';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import { ExternalLinkLine } from '@archipro-design/icons';
import ProductTagsDisplay from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFeatureFlaggedPaintPickerContext } from '@modules/paint-picker-new/hooks/useFeatureFlaggedPaintPickerContext';
import { RESENE_TOOL_ENABLED } from '@modules/page-builder/config/tool-config';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';

interface DetailSingleImageShellProps {
    children?: React.ReactNode;
    caption?: string;
    size?: string;
    className?: string;
    libraryImage?: Image;
    dimension: ColumnDimensionType;
    displayReseneTool?: boolean;
    taggingEnabled?: boolean;
    tags?: TagType[];
    uniqueApprovedTags?: TagType[];
    imageURL?: string;
    isProduct?: boolean;
    isProject?: boolean;
    /** Project detail revamp (BC-4505) — sizes the single image module to its Figma box. */
    isProjectRevamp?: boolean;
    moduleType?: string;
    inViewPort?: boolean;
    /** ImageColumn ID — needed for the boost overlay to update the column reference after upscale */
    columnId?: string;
}

const DetailSingleImageShell = ({
    children,
    caption,
    className,
    libraryImage,
    dimension,
    displayReseneTool = true,
    taggingEnabled = true,
    tags,
    uniqueApprovedTags,
    imageURL,
    isProduct = false,
    isProject = false,
    isProjectRevamp = false,
    moduleType,
    inViewPort,
    columnId,
}: DetailSingleImageShellProps) => {
    const {
        state: { isTagging },
        canCreateTag,
    } = useProductTagging();
    const isLinked = !!libraryImage?.ClickableLink || isProduct;
    const { css } = useStyles({
        dimension,
        isTagging,
        isLinked,
        moduleType,
        isProject,
        isProjectRevamp,
        inViewPort,
    });

    const {
        pickerContext: { availablePaints },
    } = useFeatureFlaggedPaintPickerContext();

    const { mobile } = useAppDisplayModeContext();

    const userCanTag = taggingEnabled && canCreateTag;

    const showTagsOnImage =
        useABTestIsOn('product_tagging_dots') && !canCreateTag;

    const displayTags =
        mobile && uniqueApprovedTags && uniqueApprovedTags?.length > 0;

    const onClickLinkedImage = () => {
        if (!libraryImage?.ClickableLink || isTagging) {
            return;
        }
        window.open(libraryImage.ClickableLink, '_blank');
    };

    const [showHoverButtons, setShowHoverButtons] = useState(true);

    const isPageBuilder =
        typeof window !== 'undefined' &&
        (window.location.pathname.includes('edit') ||
            window.location.pathname.includes('page-builder'));

    const { ref, inView } = useInView({
        rootMargin: '2000px',
        threshold: 0,
        triggerOnce: true, // Add this to prevent re-triggering
    });

    const canBoostImage = !mobile && !!libraryImage?.ID && !!imageURL;

    // Project detail revamp (BC-4691): the boost overlay wrapper must match
    // the image box or the boost UI anchors outside the image's right edge.
    // Large images span the full content width (BC-4725); medium images use
    // the 1.26x-scaled column width (same as DetailSingleImageContainer).
    const revampBoostAlignment = (() => {
        if (!isProjectRevamp) {
            return {};
        }
        if (moduleType === 'SingleImageModule') {
            return { className: 'mx-auto w-full' };
        }
        if (moduleType === 'MediumSingleImageModule' && dimension.width > 0) {
            return {
                className: 'mx-auto w-full',
                style: { maxWidth: `${(dimension.width * 1.26) / 16}rem` },
            };
        }
        return {};
    })();

    const imageContainer = (
        <div
            className={`${css(S.DetailSingleImageContainer)} ${
                className || ''
            }`}
            onClick={onClickLinkedImage}
            id="detail-single-image-shell"
            ref={ref}
        >
            {children}
            {inView ? (
                <>
                    {taggingEnabled && libraryImage?.ID && (
                        <ProductTaggingShell
                            libraryImage={libraryImage}
                            tags={tags}
                        />
                    )}

                    {showTagsOnImage &&
                        libraryImage &&
                        tags &&
                        tags.length > 0 && (
                            <ProductTagsDisplay
                                libraryImage={libraryImage}
                                tags={tags}
                                onToggle={(toggled) =>
                                    setShowHoverButtons(!toggled)
                                }
                            />
                        )}

                    {libraryImage?.ID && showHoverButtons && imageURL && (
                        <HoverButtonsImage
                            photoID={libraryImage.ID}
                            libraryItemId={
                                libraryImage.LibraryItemID ?? undefined
                            }
                            clickableLink={
                                libraryImage?.ClickableLink || undefined
                            }
                            imageURL={imageURL}
                            downloadLink={
                                libraryImage?.DownloadLink || undefined
                            }
                            hideReseneTool={
                                RESENE_TOOL_ENABLED
                                    ? !displayReseneTool ||
                                      !availablePaints ||
                                      availablePaints.length === 0
                                    : true
                            }
                            taggingEnabled={userCanTag}
                        />
                    )}
                </>
            ) : null}
            {libraryImage?.ClickableLink && (
                <div className={css(S.DetailSingleImageLinkedIcon)}>
                    <Button
                        icon={<ExternalLinkLine />}
                        size={24}
                        color="grey"
                        as="a"
                        href={libraryImage?.ClickableLink}
                        target="_blank"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className="grid h-fit">
            {canBoostImage ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImage.ID}
                    currentImageUrl={imageURL}
                    displayWidth={dimension.width}
                    displayHeight={dimension.height}
                    columnId={columnId}
                    wasUpscaled={!!libraryImage.WasUpscaled}
                    {...revampBoostAlignment}
                >
                    {imageContainer}
                </BoostResolutionOverlay>
            ) : (
                imageContainer
            )}
            {inView && (caption || displayTags) && (
                <div className={css(S.MobileCaptionContainer)}>
                    {inView && displayTags && (
                        <Flex>
                            <TagPopover
                                tags={uniqueApprovedTags}
                                dimension={dimension}
                            />
                        </Flex>
                    )}
                    {caption && (
                        <div className={css(S.ImageCaptionContainer)}>
                            <ImageCaption
                                text={caption}
                                isMain={false}
                                moduleType={moduleType}
                                isProject={isProject}
                                inViewPort={inViewPort}
                                isPageBuilder={isPageBuilder}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DetailSingleImageShell;
